import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { includes, isUndefined, trim } from 'lodash';
import { fromEvent, merge, Observable, Subject } from 'rxjs';
import { BrowserManagerDataStore } from './browser-manager-data.store';
import { WindowRefService } from 'i2g-ng-utils';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export const I2G_DEVICE_ID = 'i2gDeviceId';

const userAgentParser = require('ua-parser-js');

@Injectable()
export class BrowserManagerDataService {
  private _window: any;
  private _navigator: any;
  private _platform: string;
  private _device: string;
  private _isMobile: boolean;
  private _tabVisible: Subject<boolean> = new Subject<boolean>();

  online$: Observable<boolean>;
  tabVisible$: Observable<boolean> = this._tabVisible
    .asObservable()
    .pipe(distinctUntilChanged());

  constructor(
    private _browserManagerDataStore: BrowserManagerDataStore,
    @Inject(DOCUMENT) private _document: Document,
    windowRefService: WindowRefService
  ) {
    this._window = windowRefService.nativeWindow;
    this._navigator = this._window.navigator;

    this.online$ = merge(
      fromEvent(this._window, 'online'),
      fromEvent(this._window, 'offline')
    ).pipe(
      startWith(this._navigator.onLine),
      map(() => this._navigator.onLine),
      distinctUntilChanged()
    );

    this._document.addEventListener('visibilitychange', () =>
      this._tabVisible.next(!this._document.hidden)
    );
  }

  getUserAgent(): string {
    return this._navigator.userAgent;
  }

  getPlatform(): string {
    if (!this._platform) {
      const os = userAgentParser(this.getUserAgent()).os;

      this._platform = trim([os.name, os.version || ''].join(' '));
    }

    return this._platform;
  }

  isMacOS(): boolean {
    return includes(this.getPlatform().toLowerCase(), 'mac');
  }

  getDevice(): string {
    if (!this._device) {
      let device = userAgentParser(this.getUserAgent()).device;

      if (!!device && !!device.vendor && !!device.model) {
        device = device.vendor + '/' + device.model;

        this._device = device;
      } else {
        const browser = userAgentParser(this.getUserAgent()).browser;

        this._device = trim(
          [browser.name || '', browser.version || ''].join(' ')
        );
      }
    }

    return this._device;
  }

  isMobile(): boolean {
    if (isUndefined(this._isMobile)) {
      const deviceType: string = userAgentParser(this.getUserAgent()).device
        .type;

      this._isMobile = deviceType === 'mobile' || deviceType === 'tablet';
    }

    return this._isMobile;
  }

  isOnline(): boolean {
    return this._navigator.onLine;
  }

  isIE(): boolean {
    const browserName = userAgentParser(this.getUserAgent()).browser.name;

    return browserName === 'IE' || browserName === 'Edge';
  }

  isFirefox(): boolean {
    const browserName = userAgentParser(this.getUserAgent()).browser.name;

    return browserName === 'Firefox';
  }

  getBrowserInfoHeaders(): Observable<any> {
    return this._browserManagerDataStore.getDeviceId().pipe(
      map((deviceId: string) => {
        return {
          'x-i2g-device': this.getDevice(),
          'x-i2g-device-id': deviceId,
          'x-i2g-platform': this.getPlatform(),
          'x-i2g-client': `Web/${environment.version}`,
        };
      })
    );
  }
}
